import { useEffect, useRef } from 'react';
import L, {
  PathOptions, Point, Curve, Polyline
} from 'leaflet';
import 'leaflet-arrowheads';
import '@elfalem/leaflet-curve';
import { useMap } from 'react-leaflet';
import { NetworkTopologyStateEnum } from 'data/network-topology/network-topology.types';
import * as actions from 'data/accessFunction/accessFunction.actions';
import { RootState } from 'data/root.reducers';
import { ConnectedProps, connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { getNetworkTopologyMapConfig } from 'data/network-topology/network-topology.adapter';
import _ from 'lodash';
import { setConnectorColor } from './utils/adaptConnectorCollor';
import {
  calculateControlPoints, calculateDeltas, determineLineType, getCenters, getDistances
} from './utils/adaptConnector';
import { TooltipRow, TooltipLabelValue, TooltipCaptionText } from './utils/tooltipData';

const mapState = (state: RootState) => ({
});

const mapDispatch = {
  goToAccessFunctionView: actions.goToAccessFunctionView,
  selectAccessFunction: actions.selectAccessFunction,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>

type RouterParams = {
  history: string
}

type Props = {
  startPoint: Point;
  endPoint: Point;
  type: string;
  lineStatus: string;
  config?: PathOptions;
  tooltipData: TooltipRow[]
} & PropsFromRedux & PropsFromRouter;

const Connector = ({
  startPoint, endPoint, type, config, lineStatus, tooltipData, history, selectAccessFunction, goToAccessFunctionView
}: Props): null => {
  const defaultConfig = {
    arrowColor: '#bcc4cc',
    arrowHighlightedColor: '#4da6ff',
    controlPointsColor: '#ff4747',
    boundingBoxColor: '#ffcccc',
    dotEndingBackground: '#fff',
    dotEndingRadius: 3,
    arrowHeadEndingSize: 10,
    hoverableLineWidth: 15,
    strokeWidth: 1.5
  };
  const currentConfig = {
    ...defaultConfig,
    ...config
  };

  const {
    arrowColor,
    arrowHighlightedColor,
    controlPointsColor,
    boundingBoxColor,
    arrowHeadEndingSize,
    strokeWidth,
    hoverableLineWidth,
    dotEndingBackground,
    dotEndingRadius
  } = currentConfig;

  const map = useMap();
  //   const start = map.latLngToContainerPoint(startPoint);
  //   const end = map.latLngToContainerPoint(endPoint);
  //   const controlPoint = calculateControlPoint(start, end);

  //
  //
  //          Q2--L4----Z
  //          |
  //          L3
  //          |
  //          |
  //          L2
  //          |
  // M----L1--Q1
  //
  // M --> Start Point
  // L1 --> Point from which the Quadratic Bezier Curve starts
  // Q1 --> Quadratic Bezier Curve control point
  // L2 --> Point at which the curve ends
  // L3 --> Point to which the line continues vertically
  // Q2 --> Quadratic Bezier Curve control point
  // L4 --> Point at which the curve ends
  // Z --> End point
  //
  // L.curve([
  // 'M', [M.x, M.y],
  // 'L', [L1.x, L1.y],
  // 'Q', [Q1.x, Q1.y], [L2.x, L2.y],
  // 'L', [L3.x, L3.y],
  // 'Q', [Q2.x, Q2.y], [L4.x, L4.y],
  // 'L', [Z.x, Z.y]], { color: 'red', fill: false })
  //
  // Constants Used:
  // xdistance: Half of the horizontal distance between given two points identified by
  //              Math.abs(((startPoint.y - endPoint.y) / 2))
  // curveConstant: constant used to determine the angle for the curve. Determined by trial and error
  //                  for the current map initial zoom level.
  // Also, the curveConstant is adjusted if the vertical distance between the
  // two points is very less so that the curve is minimal.

  const mapConfig = getNetworkTopologyMapConfig();
  const zoom = map.getZoom();
  const maxZoomLevel = mapConfig.mapPosition.maxZoom;
  const { horizontalCenter, verticalCenter } = getCenters(startPoint, endPoint);
  const curveRefs = useRef<Curve[]>([]);
  const arrowRefs = useRef<Polyline<any>[]>([]);

  const color = setConnectorColor(NetworkTopologyStateEnum[lineStatus as keyof typeof NetworkTopologyStateEnum]);
  const curveOptions = {
    color,
    weight: 10,
    opacity: 0,
    fill: false,
    // lineCap: 'round',
  };

  let CONTROL_POINTS_RADIUS = 0.5;
  const { horizontalDistance, hDist, verticalDistance } = getDistances(map, startPoint, endPoint);
  let { isVerticalLine, isCurvedVerticalLine } = determineLineType(horizontalDistance, verticalDistance, hDist, zoom, maxZoomLevel);
  if (horizontalDistance < 50 || verticalDistance < 50) {
    CONTROL_POINTS_RADIUS = 0.2;
  }
  let closeIcons = false;
  if (verticalDistance < 30 && horizontalDistance < 30) {
    closeIcons = true;
  } else {
    closeIcons = false;
  }

  if (zoom > maxZoomLevel - 3) {
    CONTROL_POINTS_RADIUS = 0.1;
  }

  const boundingBoxElementsBuffer =
    strokeWidth +
    arrowHeadEndingSize / 2 +
    dotEndingRadius +
    CONTROL_POINTS_RADIUS / 2;

  const getStatusIconClass = (status: string) => {
    let ret = 'cluster-row-unknown';
    if (status === 'ACTIVE') {
      ret = 'cluster-row-active';
    } else if (status === 'INACTIVE') {
      ret = 'cluster-row-inactive';
    } else if (status === 'FAILED') {
      ret = 'cluster-row-failed';
    }
    return ret;
  }

  useEffect(() => {
    if (!map) return;
    if (!startPoint || !endPoint) return;
    const {
      absDx, absDy, dx, dy
    } = calculateDeltas(startPoint, endPoint);

    if (zoom < maxZoomLevel - 3 && absDx < 0.4) {
      CONTROL_POINTS_RADIUS = 0;
    }
    const {
      S, L1, Q1, L2, L3, Q2, L4, E
    } = calculateControlPoints({
      startPoint,
      endPoint,
      map,
      CONTROL_POINTS_RADIUS,
      isVerticalLine,
      isCurvedVerticalLine,
      horizontalCenter,
      verticalCenter,
      type
    });
    let curve;
    const cssClass = closeIcons ? 'no-display' : 'line-margin';
    const createNePopupRowitem = (label: string, value: string, container: any) => {
      const rowItemDiv = L.DomUtil.create('div', 'ne-row-item', container);
      const rowItemLabel = L.DomUtil.create('span', 'ne-row-item-label', rowItemDiv);
      rowItemLabel.innerHTML = `${label}: `;
      const rowItemValue = L.DomUtil.create('span', 'ne-row-item-value', rowItemDiv);
      rowItemValue.title = value;
      rowItemValue.innerHTML = value;
      return rowItemDiv;
    }
    const createNePopupRowCaptionitem = (caption: string, container: any) => {
      const rowItemDiv = L.DomUtil.create('div', 'ne-row-item', container);
      const rowItemSpan = L.DomUtil.create('span', 'ne-row-caption-text', rowItemDiv);
      rowItemSpan.innerHTML = caption;
      return rowItemDiv;
    }

    const handleGoToPOIPage = (data: any): void => {
      map.closePopup();
      // Object.entries(layersToRemove).map((l) => {
      //   if (!(l[1] instanceof L.MarkerCluster) && l[1] instanceof L.Marker) {
      //     map.removeLayer(l[1] as any);
      //   }
      // });
      selectAccessFunction([data]);
      goToAccessFunctionView(history);
    };

    const capitalizeWord = (word: string) => {
      if (!word) {
        return '';
      }
      return _.startCase(word.toLowerCase());
    }

    const createLinePopupContent = () => {
      const popupContainer = L.DomUtil.create('div', 'popup-container-ne');
      const neRowHeader = L.DomUtil.create('div', 'ne-row-header', popupContainer);
      const statusIconDiv = L.DomUtil.create('div', undefined, neRowHeader);
      L.DomUtil.create('span', `${getStatusIconClass(lineStatus)}`, statusIconDiv);
      const linkStatusSpan = L.DomUtil.create('span', '', neRowHeader);
      linkStatusSpan.innerHTML = `Link is ${capitalizeWord(lineStatus)}`;
      if (tooltipData.length > 0) {
        tooltipData.forEach((t: TooltipRow) => {
          if ((t as TooltipLabelValue).label) {
            createNePopupRowitem((t as TooltipLabelValue).label, (t as TooltipLabelValue).value, popupContainer);
          } else {
            createNePopupRowCaptionitem((t as TooltipCaptionText).caption, popupContainer);
          }
        })
      }
      return popupContainer;
    }

    if (isVerticalLine) {
      curve = L.curve([
        'M', [S.x, S.y],
        'L', [L1.x, L1.y],
        'Q', [Q1.x, Q1.y], [L2.x, L2.y],
        'L', [E.x, E.y]], {
        color, fill: false, lineCap: 'round', weight: defaultConfig.strokeWidth, className: cssClass
      }).addTo(map);
      isVerticalLine = false;
    } else if (isCurvedVerticalLine) {
      if (horizontalDistance < 10) {
        curve = L.curve([
          'M', [S.x, S.y],
          'C', [L1.x, L1.y], [L4.x, L4.y], [E.x, E.y]], {
          color, fill: false, lineCap: 'round', weight: defaultConfig.strokeWidth, className: cssClass
        }).addTo(map);
      } else {
        curve = L.curve([
          'M', [S.x, S.y],
          'L', [L1.x, L1.y],
          'Q', [Q1.x, Q1.y], [L2.x, L2.y],
          'L', [L3.x, L3.y],
          'Q', [Q2.x, Q2.y], [L4.x, L4.y],
          'L', [E.x, E.y]], {
          color, fill: false, lineCap: 'round', weight: defaultConfig.strokeWidth, className: cssClass
        }).addTo(map);
      }
      isCurvedVerticalLine = false;
    } else {
      curve = L.curve([
        'M', [S.x, S.y],
        'L', [L1.x, L1.y],
        'Q', [Q1.x, Q1.y], [L2.x, L2.y],
        'L', [L3.x, L3.y],
        'Q', [Q2.x, Q2.y], [L4.x, L4.y],
        'L', [E.x, E.y]], {
        color, fill: false, opacity: 1, lineCap: 'round', weight: defaultConfig.strokeWidth, className: cssClass, interactive: true
      }).addTo(map);
    }
    curveRefs.current.push(curve);

    const latlngs = curve.trace([0, 0.25, 0.5, 0.75, 1]);
    const arrow = L.polyline([latlngs], curveOptions).arrowheads({
      frequency: 'endonly',
      fill: true,
      size: `${arrowHeadEndingSize}px`,
      // offsets: { start: '15px', end: '15px' },
      color,
      className: cssClass
    });
    // arrow.bindTooltip(createLinePopupContent());
    arrow.bindPopup(createLinePopupContent(), { className: 'popup-link' });
    arrow.addTo(map);
    arrowRefs.current.push(arrow);

    return () => {
      if (curveRefs.current.length) {
        curveRefs.current.forEach((curve) => map.removeLayer(curve));
        curveRefs.current = [];
      }
      if (arrowRefs.current.length) {
        arrowRefs.current.forEach((curve) => map.removeLayer(curve));
        arrowRefs.current = [];
      }
    };
  }, [map, startPoint, endPoint]);

  return null;
};

export default withRouter((connector(Connector)));
